import { Button, ButtonProps } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import AddEntityModal from "features/entities/AddEntityModal";
import { getFundEntityTableDataRetrieveQueryKey } from "gen/endpoints/fund-entities-table-data/fund-entities-table-data";
import { getGlobalEntityTableDataRetrieveQueryKey } from "gen/endpoints/global-entity-table-data/global-entity-table-data";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { rootRoute } from "shared/navigation/Routes";

export interface AddEntityButtonProps extends ButtonProps {
  fundFamilyId?: string;
  size?: "xs" | "sm" | "md";
}

export const AddEntityButton: React.FC<AddEntityButtonProps> = ({
  fundFamilyId,
  size,
  ...props
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["entity"]);
  const { showToast } = useToast();

  const confirmAddEntity = useConfirmationDialog(AddEntityModal, {
    fundFamilyId,
    onSuccess: () => {
      showToast({ message: t("entity:add.success"), variant: "success" });
      void queryClient.invalidateQueries({
        queryKey: getGlobalEntityTableDataRetrieveQueryKey(),
      });
      if (fundFamilyId) {
        void queryClient.invalidateQueries({
          queryKey: getFundEntityTableDataRetrieveQueryKey(fundFamilyId),
        });
      }
    },
  });
  const interEntityFundStructVisualizationOn = useFeatureFlagActive(
    "inter_entity_fund_structure",
  );

  const useFundVisualization =
    fundFamilyId && interEntityFundStructVisualizationOn;
  const navigate = useNavigate();

  return (
    <Restricted permission={PermissionTypeEnum.config}>
      <Button
        onClick={async () => {
          if (useFundVisualization) {
            navigate(
              rootRoute({})
                .fundFamilies({})
                .fundFamily({
                  fundFamilyId,
                })
                .setup({})
                .createEntity({}).$,
            );
          } else {
            await confirmAddEntity();
          }
        }}
        size={size}
        {...props}
      >
        {t("entity:createEntity")}
      </Button>
    </Restricted>
  );
};
